import { configureStore } from '@reduxjs/toolkit'
import type { TypedUseSelectorHook } from 'react-redux'
import { useDispatch, useSelector } from 'react-redux'

import adminDashboardSlice from './slices/admin-dashboard-slice'
import adminUserManagementSlice from './slices/admin-user-management.slice'
// Import slices
import authSlice from './slices/auth_slice'
import cardsSlice from './slices/cards.slice'
import citizenSearchSlice from './slices/citizen-search.slice'
// New slice
import completedEnrollmentSlice from './slices/completed-enrollment.slice'
import dashboardSlice from './slices/dashboard-slice'
import enrollmentSlice from './slices/enrollment.slice'
import notificationSlice from './slices/notification_slice'
import pendingEnrollmentSlice from './slices/pending-enrollment.slice'
// import policeCaseManagementSlice from './slices/police-case-management.slice'
// import policeDashboardSlice from './slices/police-dashboard.slice'
import policeVerificationSlice from './slices/police-verification.slice'
import taskManagementSlice from './slices/task-management.slice'
import uiSlice from './slices/ui_slice'
import verificationSlice from './slices/verification.slice'
import walletSlice from './slices/wallet.slice'

export const store = configureStore({
  reducer: {
    auth: authSlice,
    ui: uiSlice,
    notification: notificationSlice,
    enrollment: enrollmentSlice,
    verification: verificationSlice,
    dashboard: dashboardSlice,
    adminDashboard: adminDashboardSlice,
    // policeDashboard: policeDashboardSlice,
    citizenSearch: citizenSearchSlice,
    pendingEnrollment: pendingEnrollmentSlice,
    completedEnrollment: completedEnrollmentSlice,
    adminUserManagement: adminUserManagementSlice,
    taskManagement: taskManagementSlice,
    policeVerification: policeVerificationSlice,
    // policeCaseManagement: policeCaseManagementSlice,
    cards: cardsSlice,
    wallet: walletSlice,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Ignore these action types
        ignoredActions: [
          'persist/PERSIST',
          'persist/REHYDRATE',
          'auth/setCredentials',
          'enrollment/initializeEnrollment',
          'enrollment/savePersonalDetails',
          'dashboard/setCustomDateRange',
          'adminDashboard/setCustomDateRange',
          'pendingEnrollment/setSearchResults',
          'pendingEnrollment/updateEnrollmentInResults',
          'completedEnrollment/setSearchResults',
          'completedEnrollment/updateEnrollmentInResults',
          'adminUserManagement/setUsers',
          'adminUserManagement/setFilters',
          'adminUserManagement/setSelectedUsers',
          'adminUserManagement/addUser',
          'adminUserManagement/updateUser',
          'adminUserManagement/removeUser',
          'adminUserManagement/removeUsers',
          'taskManagement/setTasks',
          'taskManagement/setFilters',
          'taskManagement/setSelectedTasks',
          'taskManagement/addTask',
          'taskManagement/updateTask',
          'taskManagement/removeTask',
          'taskManagement/removeTasks',
          'policeVerification/setVerifications',
          'policeVerification/setFilters',
          'policeVerification/setSelectedVerifications',
          'policeVerification/addVerification',
          'policeVerification/updateVerification',
          'policeVerification/removeVerification',
          'policeVerification/removeVerifications',
          'policeCaseManagement/setCases',
          'policeCaseManagement/setFilters',
          'policeCaseManagement/setSelectedCases',
          'policeCaseManagement/addCase',
          'policeCaseManagement/updateCase',
          'policeCaseManagement/removeCase',
          'policeCaseManagement/removeCases',
        ],
        // Ignore these field paths in all actions
        ignoredActionPaths: [
          'meta.arg',
          'payload.timestamp',
          'auth.user.lastLogin',
          'enrollment.sessionExpiresAt',
          'dashboard.lastRefreshed',
          'dashboard.cachedData',
          'adminDashboard.savedViews',
          'pendingEnrollment.lastRefreshed',
          'completedEnrollment.lastRefreshed',
          'completedEnrollment.results.summary',
          'adminUserManagement.lastRefresh',
          'adminUserManagement.cacheTimestamp',
          'adminUserManagement.filters.dateFrom',
          'adminUserManagement.filters.dateTo',
          'taskManagement.lastRefreshed',
          'taskManagement.cacheTimestamp',
          'taskManagement.filters.dateFrom',
          'taskManagement.filters.dateTo',
          'policeVerification.lastRefreshed',
          'policeVerification.cacheTimestamp',
          'policeVerification.filters.dateFrom',
          'policeVerification.filters.dateTo',
          'policeCaseManagement.lastRefreshed',
          'policeCaseManagement.cacheTimestamp',
          'policeCaseManagement.filters.dateFrom',
          'policeCaseManagement.filters.dateTo',
        ],
        // Ignore these paths in the state
        ignoredPaths: [
          'items.dates',
          'dashboard.customDateRange',
          'dashboard.bookmarkedViews',
          'adminDashboard.savedViews',
          'pendingEnrollment.lastRefreshed',
          'pendingEnrollment.results.summary',
          'adminUserManagement.lastRefresh',
          'adminUserManagement.cacheTimestamp',
          'adminUserManagement.filters.dateFrom',
          'adminUserManagement.filters.dateTo',
          'taskManagement.lastRefreshed',
          'taskManagement.cacheTimestamp',
          'taskManagement.filters.dateFrom',
          'taskManagement.filters.dateTo',
          'policeVerification.lastRefreshed',
          'policeVerification.cacheTimestamp',
          'policeVerification.filters.dateFrom',
          'policeVerification.filters.dateTo',
          'policeCaseManagement.lastRefreshed',
          'policeCaseManagement.cacheTimestamp',
          'policeCaseManagement.filters.dateFrom',
          'policeCaseManagement.filters.dateTo',
        ],
      },
    }),

  devTools: process.env.NODE_ENV !== 'production',
})

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>
export type AppDispatch = typeof store.dispatch

// Use throughout your app instead of plain `useDispatch` and `useSelector`
export const useAppDispatch: () => AppDispatch = useDispatch
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

// RTK Query setup listeners removed - using TanStack Query now

export const { dispatch, getState, subscribe, replaceReducer } = store

export const selectAdminUserManagementState = (state: RootState) => state.adminUserManagement

// Helper function to check if admin user management features are available
export const isAdminUserManagementEnabled = (userRole?: string) => {
  const allowedRoles = ['admin', 'regional_admin', 'woreda_admin', 'kebele_admin']
  return userRole ? allowedRoles.includes(userRole.toLowerCase()) : false
}

// Cache management utilities now handled by TanStack Query
// Individual hook invalidation can be used instead of global cache clearing
